import { useDataChainId } from '@ui/hooks/datachain';
import { useSimulateMutation } from '@ui/hooks/useSimulateMutation';
import { useTxnWriteContract } from '@ui/hooks/useTxnWriteContract';
import { getSwapFacet } from '@ui/services/contract/contract-instances/getSwapFacet';
import { Token } from '@ui/types/token';
import { TxnMutateCallBackParams } from '@ui/types/tx';
import { useCallback, useMemo } from 'react';
import { Address, parseUnits, zeroAddress } from 'viem';
import { useAccount,usePublicClient, useWalletClient } from 'wagmi';

type SwapParams = {
  inputToken: Token;
  outputToken: Token;
  inputAmount: string;
  minOutputAmount: string;
  adapter: Address;
  adapterData: `0x${string}`;
  receiver: Address;
};

export const useSwap = (params?: TxnMutateCallBackParams) => {
  const chainId = useDataChainId();
  const publicClient = usePublicClient({ chainId });
  const { data: walletClient } = useWalletClient({ chainId });
  const { address } = useAccount();

  const { data, writeContract, isPending } = useTxnWriteContract({
    onSuccess: params?.onSuccess,
    onError: params?.onError,
    onSettled: params?.onSettled,
    onReceiptSettled: params?.onReceiptSettled,
  });

  const { isSimulating, simulate } = useSimulateMutation();

  const write = useCallback(async ({
    inputToken,
    outputToken,
    inputAmount,
    minOutputAmount,
    adapter,
    adapterData,
    receiver,
  }: SwapParams) => {
    if (!publicClient || !walletClient) {
      console.warn('Wallet not connected');
      
return;
    }

    const swapFacet = getSwapFacet({
      chainId,
      publicClient: publicClient as any,
      walletClient,
    });

    const amountIn = parseUnits(inputAmount, inputToken.decimals);
    const minAmountOut = parseUnits(minOutputAmount, outputToken.decimals);

    // Fee is 0.5% of input
    const fee = 0n;
    const amountAfterFee = amountIn - fee;

    // Fee Recipient
    const feeRecipient = '0xf9bd99A8e5bE72AdF687B304eD0e0234E0eBCcBa';

    // Construct the swap step
    const isNativeIn = inputToken.isNative;
    const tokenIn = isNativeIn ? zeroAddress : (inputToken.address as Address);

    const step = {
      adapter,
      tokenIn,
      tokenOut: outputToken.address as Address,
      amountIn: amountAfterFee,
      minAmountOut, // Slippage applied here
      data: adapterData,
    };

    const args = [
      tokenIn,
      outputToken.address as Address,
      amountIn,
      minAmountOut,
      feeRecipient, // feeRecipient
      fee, // feeAmount
      [step], // steps array
    ] as const;
    
    const value = isNativeIn ? amountIn : 0n;

    const { isSuccess } = await simulate(async () => {
      await swapFacet.estimateGas.executeSwap(args as any, {
        account: address,
        value,
      });
    });

    if (!isSuccess) {
        console.error("Simulation failed");
      
return;
    }

    return writeContract({
      abi: swapFacet.abi,
      address: swapFacet.address,
      functionName: 'executeSwap',
      args: args,
      value: value,
    });
  }, [chainId, publicClient, walletClient, simulate, writeContract, address]);

  return useMemo(() => {
    return {
      data,
      mutate: write,
      isPending: isSimulating || isPending,
    };
  }, [data, isPending, isSimulating, write]);
};
